import type { JsonApiSpecResponse } from '../types';

/**
 * Creates a transformer function that adds UI-specific properties to JSON API response data.
 *
 * This function generates a new property for each key in the `keys` array by prefixing it with "ui_"
 * and applying the provided callback function to transform the original value.
 *
 * @param keys - Array of property keys to transform into UI properties. Defaults to empty array.
 * @param callback - Function to transform the original value into the UI property value.
 *                  Defaults to a function that returns undefined.
 * @returns A transformer function that processes JsonApiSpecResponse objects
 *
 * @example
 * ```typescript
 * const transformer = createUiProps(['name', 'status'], (value) => value?.toUpperCase());
 * const result = transformer({ data: { name: 'john', status: 'active' } });
 * // Result: { data: { name: 'john', status: 'active', ui_name: 'JOHN', ui_status: 'ACTIVE' } }
 * ```
 */
export function createUiProps(
  keys: string[] = [],
  callback = (value: any): any => undefined
) {
  return ({ data, ...rest }: JsonApiSpecResponse): JsonApiSpecResponse => {
    if (Array.isArray(data)) {
      return {
        data: data.map(
          (d: { [k: string]: any }) => ({
            ...d,
            ...keys.reduce(
              (acc, key: string) => ({
                ...acc,
                [`ui_${key}`]: callback(d[key]),
              }),
              {}
            ),
          }),
          {}
        ),
        ...rest,
      };
    }

    if (typeof data === 'object') {
      return {
        data: {
          ...data,
          ...keys.reduce(
            (acc, key: string) => ({
              ...acc,
              [`ui_${key}`]: callback(data[key]),
            }),
            {}
          ),
        },
      };
    }

    return { data, ...rest };
  };
}

/**
 * Transforms currency-formatted strings to numbers by removing non-numeric characters
 * @param value - The value to transform (string, number, or undefined)
 * @returns The transformed number or undefined if invalid
 */
export const transformCurrencyToNumber = (
  value: string | number | undefined
): number | undefined => {
  if (typeof value === 'number') return value;
  if (!value) return undefined;

  const result = parseFloat(String(value).replace(/[^\d.-]/g, ''));
  return isNaN(result) ? undefined : result;
};
